import {
  Box,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ENTER_TOUCH_DELAY, LEAVE_TOUCH_DELAY } from "./LightTooltip";

import { PatientDetailData } from "../features/data/patientApiSlice";
import { computeReadableValue } from "../utils/strings";
import { createStyleSheet } from "../utils/styles";
import { formatDateTime } from "../utils/dates";
import { formatDistanceToNow } from "date-fns";
import { theme } from "../../theme";
import { useTranslation } from "react-i18next";

const RemindersList = (props: {
  patient: PatientDetailData;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClickAway: () => void;
}) => {
  const { patient, open, anchorEl, onClickAway } = props;
  const { t } = useTranslation("Patient");

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClickAway}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      slotProps={{ paper: { sx: styles.arrow } }}
    >
      <Box sx={styles.container}>
        <Typography variant="detailEntryTitle" color="primary">
          {t("remindersTitle")}
        </Typography>
        <Table>
          <TableBody>
            <TableRow
              sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
            >
              <TableCell sx={styles.plainCell} width={164}>
                <Typography variant="detailCellBody">
                  {t("reminderType")}
                </Typography>
              </TableCell>
              <TableCell sx={styles.plainCell} width={120}>
                <Typography variant="detailCellBody">
                  {t("reminderDueDate")}
                </Typography>
              </TableCell>
              <TableCell sx={styles.plainCell} width={164}>
                <Typography variant="detailCellBody">
                  {t("reminderCycle")}
                </Typography>
              </TableCell>
              <TableCell sx={styles.plainCell} width={120}>
                <Typography variant="detailCellBody">
                  {t("reminderLocation")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow sx={styles.rowGap} />
            {patient.reminders?.map((reminder, index) => (
              <TableRow key={reminder.type + reminder.next_due_date + index}>
                <TableCell sx={styles.plainCell}>
                  <Typography variant="detailEntryBody">
                    {computeReadableValue(reminder.type)}
                  </Typography>
                </TableCell>
                <TableCell sx={styles.plainCell}>
                  <Tooltip
                    title={formatDateTime(new Date(reminder.next_due_date))}
                    enterTouchDelay={ENTER_TOUCH_DELAY}
                    leaveTouchDelay={LEAVE_TOUCH_DELAY}
                    onContextMenu={(event) => event.preventDefault()}
                  >
                    <Typography variant="detailEntryBody">
                      {formatDistanceToNow(new Date(reminder.next_due_date), {
                        addSuffix: true,
                      })}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={styles.plainCell}>
                  <Typography variant="detailEntryBody">
                    {computeReadableValue(reminder.cycle)}
                  </Typography>
                </TableCell>
                <TableCell sx={styles.plainCell}>
                  <Typography variant="detailEntryBody">
                    {reminder.location || t("common:na")}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Menu>
  );
};

const styles = createStyleSheet({
  arrow: {
    marginTop: 1,
    overflow: "visible",
    "&:before": {
      content: '""',
      display: "block",
      position: "absolute",
      top: 0,
      left: "calc(50% - 5px)",
      width: 10,
      height: 10,
      backgroundColor: "inherit",
      transform: "translateY(-50%) rotate(45deg)",
      boxShadow: "-3px -3px 5px -2px rgba(0,0,0,0.1)",
    },
  },
  container: {
    px: 2,
    py: 1,
  },
  rowGap: {
    height: 4,
  },
  plainCell: {
    padding: 0,
    borderBottom: "none",
    verticalAlign: "top",
  },
});

export default RemindersList;
